/**
 * Scrap Management React Hooks
 * Epic 65: Selejtezés & Készletkorrekció
 *
 * TanStack Query hooks for scrap records and protocols.
 */

import {
  approveScrapProtocol,
  createScrapProtocol,
  createScrapRecord,
  finalizeScrapProtocol,
  listScrapProtocols,
  listScrapRecords,
  type ScrapRecordQueryResult,
  type ScrapReportQueryResult,
} from '@/api/scrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function useScrapRecords(params?: {
  warehouseId?: string;
  productId?: string;
  reason?: string;
}) {
  const query = useQuery({
    queryKey: ['scrap', 'records', params],
    queryFn: async () => {
      const res = await listScrapRecords(params);
      return res.data;
    },
  });

  return {
    result: query.data as ScrapRecordQueryResult | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useCreateScrapRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      inventoryItemId: string;
      productId: string;
      warehouseId: string;
      quantity: number;
      reason: string;
      note?: string;
      photoUrl?: string;
      unitValue?: number;
    }) => createScrapRecord(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scrap', 'records'] });
    },
  });
}

export function useScrapProtocols(params?: { year?: number; month?: number; status?: string }) {
  const query = useQuery({
    queryKey: ['scrap', 'protocols', params],
    queryFn: async () => {
      const res = await listScrapProtocols(params);
      return res.data;
    },
  });

  return {
    result: query.data as ScrapReportQueryResult | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useCreateScrapProtocol() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { year: number; month: number }) => createScrapProtocol(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scrap', 'protocols'] });
    },
  });
}

export function useApproveScrapProtocol() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => approveScrapProtocol(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scrap', 'protocols'] });
    },
  });
}

export function useFinalizeScrapProtocol() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => finalizeScrapProtocol(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scrap', 'protocols'] });
    },
  });
}
